import type { NavigationInput } from "../lib/navigation.types.js";
import type {
  NavigationParameters,
  ParameterOptions,
  ParameterRecord,
  ParametersDefinition,
  ParamType,
} from "./params.types.js";
import {
  isValidKey,
  type KeyFilter,
  type ObjectToEntries,
  type ObjectToEntry,
} from "../lib/utils.js";
import { baseLocation } from "../lib/location.js";
import { locationToParams } from "./params.input.js";
import {
  coerceValue,
  appendEntries,
  getObjectReducer,
  mergeParamCache,
  paramTypes,
  toEntries,
  toStringParams,
  typeDelimiterMap,
  uncoerceValue,
  deleteValue,
  getOptions,
  sanitizeEmpty,
  EMPTY,
} from "./params.utils.js";

/**
 *
 *
 *
 *
 *
 *
 *
 *
 *
 */
export const createParams = <P extends ParameterRecord>(
  userLocation: NavigationInput,
  type: ParamType,
  filter: KeyFilter[] = [],
  base: Location,
  userOptions: ParameterOptions = {},
): NavigationParameters<P> => {
  let loc = userLocation;
  let [url, raw] = locationToParams(loc, type, base);

  /*                                         */
  const options: ParameterOptions = { decode: true, ...userOptions };

  /**
 *
 *
 *
 */
  const deletedKeys = new Set<string>();
  const markDeleted = (k: string): void => {
    deletedKeys.add(k);
  };

  const self: NavigationParameters<P> = {
    /*              */

    get [Symbol.toStringTag]() {
      return `${type[0].toUpperCase()}${type.slice(1)}Parameters`;
    },

    get type() {
      return type;
    },

    get deletedKeys() {
      return new Set(deletedKeys);
    },

    get raw() {
      return new Map(raw);
    },

    /*        */

    /*               */

    *[Symbol.iterator]() {
      yield* toEntries(raw, options, filter) as ObjectToEntry<P>[];
    },

    *entries(o) {
      yield* toEntries(raw, getOptions(options, o), filter) as ObjectToEntry<P>[];
    },

    *keys() {
      for (const key of raw.keys()) {
        if (isValidKey(key, filter)) yield key as keyof P;
      }
    },

    *values(o) {
      const opts = getOptions(options, o);
      for (const [key, values] of raw) {
        if (isValidKey(key, filter))
          for (const valueData of values) {
            const value = sanitizeEmpty(valueData);
            yield (opts.decode ? decodeURI(value) : value) as P[keyof P];
          }
      }
    },

    /*        */

    /*             */

    has: (k) => {
      return isValidKey(k as string, filter) && raw.has(k as string);
    },

    get: (k, o) => {
      if (!self.has(k)) return null;
      const value = sanitizeEmpty(raw.get(k as string)![0]);
      return getOptions(options, o).decode ? decodeURI(value) : value;
    },

    getAll: (k, o) => {
      if (!self.has(k)) return [];
      const valuesData = raw.get(k as string)!;
      const values = valuesData.map(sanitizeEmpty);
      return getOptions(options, o).decode ? values.map(decodeURI) : values;
    },

    coerce: (k, o) => {
      return coerceValue(self.get(k, o));
    },

    coerceAll: (k, o) => {
      return self.getAll(k, o).map((v) => coerceValue(v));
    },

    /*        */

    /*                   */

    set: (k, v = EMPTY as never) => {
      if (!isValidKey(k as string, filter)) return;
      raw.set(k as string, [uncoerceValue(v)]);
    },

    append: (k, v = EMPTY as never) => {
      if (!isValidKey(k as string, filter)) return;
      if (!raw.has(k as string)) raw.set(k as string, []);
      raw.get(k as string)!.push(uncoerceValue(v));
    },

    delete: (k, v) => {
      if (!isValidKey(k as string, filter)) return;
      deleteValue(raw, k as string, v);

      /*                                                                                   */
      /*                                       */
      markDeleted(k as string);
    },

    /*        */

    /*                    */

    clear: () => {
      /*                                                    */
      for (const key of [...raw.keys()]) {
        raw.delete(key);
        if (!isValidKey(key, filter)) continue;
        markDeleted(key);
      }

      return self;
    },

    appendMany: (...entries) => {
      for (const [key, value] of entries) {
        self.append(key, value);
      }
      return self;
    },

    fromEntries: (entries, o = {}) => {
      if (!o.patch) self.clear();
      appendEntries(raw, entries, o.missing);
      return self;
    },

    fromObject: (entries, o = {}) => {
      if (!o.patch) self.clear();
      appendEntries(raw, Object.entries(entries) as ObjectToEntries<unknown>, o.missing);
      return self;
    },

    fromString: (params, o = {}) => {
      if (!o.patch) self.clear();

      for (const [key, value] of locationToParams(params, type, base)[1]) {
        if (!o.missing || !raw.has(key)) raw.set(key, value);
      }

      return self;
    },

    /*        */

    /*                 */

    toEntries: (current, o) => {
      const opts = getOptions(options, o);

      if (!current) {
        return [...self.entries(opts)] as never;
      }

      /*                                                     */
      /*                                                   */
      const params = mergeParamCache(
        locationToParams(current, type, base)[1],
        raw,
        filter,
        deletedKeys,
      );

      return toEntries(params, opts);
    },

    toRecord: (current, o) => {
      return self.toEntries(current, o).reduce(getObjectReducer(), {}) as never;
    },

    toObject: (current, o) => {
      return self.toEntries(current, o).reduce(getObjectReducer(coerceValue), {}) as never;
    },

    toString: (current) => {
      let result: string;

      if (current) {
        const curParams = locationToParams(current, type, base)[1];
        const resParams = mergeParamCache(curParams, raw, filter, deletedKeys);
        result = toStringParams(resParams);
      } else {
        result = toStringParams(raw, filter);
      }

      return result ? typeDelimiterMap[type] + result : "";
    },

    toUrl: (current) => {
      if (!current) {
        const resUrl = new URL(url);
        resUrl[type] = self.toString();
        return resUrl;
      }

      const [resUrl, curParams] = locationToParams(current, type, base);
      const params = mergeParamCache(curParams, raw, filter, deletedKeys);

      resUrl[type] = toStringParams(params);
      return resUrl;
    },

    /*        */

    /*                     */

    parse: (schema, params) => {
      return schema.parse(self.toRecord(), params);
    },

    parseSafe: (schema, params) => {
      return schema.safeParse(self.toRecord(), params);
    },

    /*        */

    /*                     */

    clone: () => {
      return createParams(toStringParams(raw), type, filter, base);
    },

    reset: (current) => {
      loc = current ?? loc;
      [url, raw] = locationToParams(loc, type, base);
      deletedKeys.clear();
      return self;
    },

    /*        */
  };

  return self;
};

/**
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 */
export const searchParams = <P extends ParameterRecord>(
  location: NavigationInput,
  filter?: string[],
  options?: ParameterOptions,
): NavigationParameters<P> => {
  return createParams(location, paramTypes[0], filter, baseLocation, options);
};

/**
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 */
export const hashParams = <P extends ParameterRecord>(
  location: NavigationInput,
  filter?: string[],
  options?: ParameterOptions,
): NavigationParameters<P> => {
  return createParams(location, paramTypes[1], filter, baseLocation, options);
};

/**
 *
 *
 *
 *
 *
 *
 */
export const paramsMap = <P extends ParameterRecord>(
  keys: (keyof P | RegExp)[],
): ParametersDefinition<P, true> => keys as never;
